import earcut from 'earcut';
import * as THREE from 'three';
import { BufferGeometry, Float32BufferAttribute, Material, Mesh, Object3D, Vector3 } from 'three';
import _ from 'underscore';
import { v4 } from 'uuid';

import {
  CellGridVolumeModel,
  DynamicAssetType,
  PrismVolumeModel,
} from '../../../../typings/api/skymap/rest/v0/.common';
import { consequetivePairIterator } from '../../utils/generators';
import { vectorToSlopePercent } from '../../utils/math/slope';
import { isDefined } from '../../utils/variables';
import { AssetManager } from '../asset-manager';
import { SkyviewCamera } from '../cameras/types';
import { CoordinateManager } from '../coordinate-manager';
import { publish, SubscriptionTopic } from '../messaging/pubsub';
import { renderOrders } from '../render-orders';
import { MouseInputData } from '../utilities/input-event/input-event-data-extractor';
import * as mathHelpers from '../utilities/math-helpers';
import { Point2d } from '../utilities/math-helpers';
import { heightLabelUtils } from './components/height-labels';
import { AssetLoadingStatus, AssetType, ElementObj } from './element-obj';
import { HasVolumes } from './interfaces/has-cell-grid-volumes';
import { HasOpacity } from './interfaces/has-opacity';
import { Sphere, SphereSprite } from './supportGeom/sphere';
import { CellGridVolume } from './volume/cell-grid-volume';
import { PrismVolume } from './volume/prism-volume';
import { VolumeHandler } from './volume/volume-handler';

const areaColor = 0xff3333;
const lineColor = 0xffff00;
const areaOpacity = 0.7;

/** The colors lines and areas are drawn with when they aren't selected. */
export const geometryStyle = { lineColor, areaColor, areaOpacity } as const;

const selectionColor = 0x88ff88;

const areaRenderOrder = renderOrders.geometryAsset.mesh;
const lineRenderOrder = renderOrders.geometryAsset.line;

interface GeometryOptions {
  assetId?: string;
  name: string;
  visible: boolean;
  isArea?: boolean;
  cellGridVolumes?: CellGridVolumeModel[];
  prismVolumes?: PrismVolumeModel[];
  skyMapVolumeSourceIds?: string[];
  sharedViewId?: string;
  assetTagIds?: string[];
  timelineContainerId?: string;
}

type GeometryPoint = {
  id: string;
  point: THREE.Vector3;
};

/**
 * @param p1
 * @param p2
 * @param material
 * @description Takes two points and creates a THREE.Line geometry
 */
function createLine(p1: THREE.Vector3, p2: THREE.Vector3, material: THREE.Material): THREE.Line {
  const geometry = new BufferGeometry();
  geometry.setAttribute(
    'position',
    new Float32BufferAttribute([...p1.toArray(), ...p2.toArray()], 3),
  );
  geometry.setIndex([0, 1]);

  const line = new THREE.Line(geometry, material);
  return line;
}

/////////////////////////////////////////////
///
/// @class Geometry
///

export class Geometry extends ElementObj implements HasOpacity, HasVolumes {
  cellGridVolumes: CellGridVolume[] = [];
  prismVolumes: PrismVolume[] = [];
  skyMapVolumeSourceIds: string[] = [];
  volumeHandler = new VolumeHandler(this);

  isArea = false;
  wgs84Coords: THREE.Vector3[] = [];
  areaMesh?: THREE.Mesh;

  private points: GeometryPoint[] = [];

  private snapping = {
    snapped: false,
    index: -1,
  };

  private oldPickedPosition?: THREE.Vector3;

  private slope = 0;
  private distance?: number;
  private planeDistance?: number;

  private area?: number;
  private planeArea?: number;

  private materials = {
    line: new THREE.LineBasicMaterial({
      color: lineColor,
      transparent: false,
      opacity: 1.0,
      depthTest: true,
    }),
    area: new THREE.MeshBasicMaterial({
      color: areaColor,
      transparent: true,
      opacity: areaOpacity,
      side: THREE.DoubleSide,
      depthTest: true,
    }),
  };

  private renderLines = true;
  private selectionSpheres: Sphere[] = [];
  private lines: THREE.Line[] = [];

  objType: DynamicAssetType = 'Geometry';

  private _enabled = true;

  get editable() {
    return this._enabled && !isDefined(this.volumeHandler?.activeVolume);
  }

  get highlightedIndex() {
    return this.highlighted;
  }

  get opacity() {
    return this._opacity;
  }

  set opacity(opacity: number) {
    this.setOpacity(opacity);

    this._opacity = opacity;
  }

  private highlighted = -1;
  private _opacity = 1;
  private _wgs84Points: Vector3[] = [];

  onMetaDataUpdated?: () => void;

  constructor(wgs84Points?: THREE.Vector3[], options?: GeometryOptions) {
    super(options?.timelineContainerId!);

    options = options ?? { name: 'Ej namngiven', visible: true };

    this.downloadable = true;

    this.freeze = {
      translate: false,
      rotateZ: true,
      rotateXY: true,
      scale: true,
    };

    this.assetId = options.assetId;
    this.name = options.name ?? 'Ej namngiven';
    this.isArea = !!options.isArea;
    this.cellGridVolumes = CellGridVolume.fromPropertyArray(options.cellGridVolumes);
    this.prismVolumes = PrismVolume.fromPropertyArray(options.prismVolumes);
    this.skyMapVolumeSourceIds = options.skyMapVolumeSourceIds ?? [];
    this.volumeHandler.activeVolume = this.cellGridVolumes[0] ?? this.prismVolumes[0];

    this._wgs84Points = wgs84Points ?? [];

    /**
     * Note:
     * Set the visibility state without triggering onVisibilityChanged event.
     * This is necessary to avoid triggering loadGeometry during the
     * initialize scene step which will cause an error because the
     * coordinate manager is initialized in parallel with the asset manager
     * which will cause a race condition.
     *
     * This issue can be resolved by decoupling the scene origo from the geodata
     * which would allow us to load the origo and projection first without loading
     * all the geodata first. If that is resolved we can call this.setStateVisibility
     * here which would then load the geometry automatically if the asset is visible.
     */
    const visible = options.visible === false ? false : true;
    void this.setStateVisibility(visible);
    this._assetTagIds = options.assetTagIds ?? [];
  }

  initialLoadingStatus(): AssetLoadingStatus {
    return 'idle';
  }

  get assetType(): AssetType {
    return this.isArea ? 'area' : 'line';
  }

  isReadyToLoad() {
    return this.loadingStatus === 'idle' || this.loadingStatus === 'error';
  }

  loadGeometry() {
    if (!this.isReadyToLoad()) {
      return;
    }

    try {
      this.loadingStatus = 'loading';

      for (const p of this._wgs84Points) {
        this.addPoint(p, true);
      }

      this.loadingStatus = 'loaded';
    } catch (err) {
      this.loadingStatus = 'error';
    } finally {
      publish(SubscriptionTopic.AssetChanged, this);
    }
  }

  hasLoadedGeometry() {
    return this.points.length > 0;
  }

  onStateVisibilityChanged() {
    if (this.assetId && this.visible && this.isReadyToLoad()) {
      this.loadGeometry();
    }

    return Promise.resolve();
  }

  /**
   * Returns a value indicating whether the context menu should be displayed at the given position.
   * @param x
   * @param y
   * @param camera
   */
  showContextMenu(x: number, y: number, camera: SkyviewCamera) {
    const sphereIndex = this.getPointIndex(x, y, camera);
    return this.isEditableSphere(sphereIndex) && this.points.length > this.minPoints();
  }

  /**
   * Returns a list of menu items to display in context menu.
   * @param x
   * @param y
   * @param camera
   */
  getContextMenuItems(x: number, y: number, camera: SkyviewCamera) {
    return [
      {
        title: 'Ta bort punkt',
        click: async () => {
          const pointIndex = this.getPointIndex(x, y, camera);
          if (pointIndex >= 0) {
            this.removePoint(pointIndex);
            await this.onChange();
          }
        },
      },
    ];
  }

  /**
   * Returns a value indicating the min required points for the current type of geometry.
   */
  minPoints() {
    return this.isArea ? 3 : 2;
  }

  /**
   * Returns index of sphere if specified position intersects with one, otherwise -1 is returned.
   * @param x
   * @param y
   * @param camera
   */
  getPointIndex(x: number, y: number, camera: SkyviewCamera) {
    return this.pick(x, y, camera);
  }

  /**
   * Returns a value indicating whether the sphere represented by specified index is an editable sphere (not a ghost sphere).
   * @param sphereIndex
   */
  isEditableSphere(sphereIndex: number) {
    return sphereIndex >= 0 && sphereIndex < this.points.length;
  }

  dispose() {
    this.children.forEach((child: Object3D | Mesh) => {
      this.remove(child);
      if (child instanceof Mesh) {
        child.geometry.dispose();
      }
    });
  }

  setEnabled(enabled: boolean) {
    this._enabled = enabled;
    this.freeze.translate = !enabled;

    if (this.editable) {
      this.showSpheres();
    } else {
      this.hideSpheres();
    }
  }

  setDepthTest(state: boolean) {
    this.materials.line.depthTest = state;
    this.materials.area.depthTest = state;
  }

  getDepthTest() {
    return this.materials.line.depthTest;
  }

  getSaveData() {
    if (!this.hasLoadedGeometry()) {
      this.loadGeometry();
    }

    const projection = CoordinateManager.getInstance().getProjectProjection();
    const worldPoints = this.getWorldPoints();
    const coordinates = worldPoints.map((point) => {
      // if a projection is set, convert to WGS84 coordinates
      if (projection) {
        const ppp = CoordinateManager.getInstance().localToProject(point.x, point.y);
        const wgs84Pos = CoordinateManager.getInstance().convertFromProject(ppp.x, ppp.y, 'wgs84');
        const pointMAS = this.calculateMAS(point.z);

        return { lng: wgs84Pos.x.toString(), lat: wgs84Pos.y.toString(), height: pointMAS };
      } else {
        // Else, just save the local coordinates
        return { lng: point.x.toString(), lat: point.y.toString(), height: point.z };
      }
    });

    const saveData = {
      type: this.objType,
      properties: {
        name: this.name,
        visible: this.getStateVisibility(),
        isArea: this.isArea,
      },
      coordinates,
      id: this.assetId,
      timelineContainerId: this.timelineContainerId,
    };

    return Promise.resolve(saveData);
  }

  // To ensure that we use get the id after an asset have been saved
  onSaved(data?: any) {
    if (!data) {
      return;
    }

    this.assetId = data.id || this.assetId;
    this.timelineContainerId = data.timelineContainerId;
  }

  getNativeAssetData() {
    const nativeGeomData = {
      uuid: this.uuid,
      name: this.name,
      visible: this.visible,
      distance: this.distance,
      planeDistance: this.planeDistance,
    };

    if (this.area) {
      Object.assign(nativeGeomData, { area: this.area });
    }
    if (this.planeArea) {
      Object.assign(nativeGeomData, { planeArea: this.planeArea });
    }

    return nativeGeomData;
  }

  /**
   * @param camera
   * @param event
   * @description Returns the geometry point that is the target of transformation.
   */
  pickTransformationPointIndex(camera: SkyviewCamera, event?: MouseInputData) {
    let selectedIndex = 0;

    if (event) {
      selectedIndex = this.getPointIndex(
        event.pointer.position.x,
        event.pointer.position.y,
        camera,
      );
    }

    return selectedIndex >= 0 ? selectedIndex : undefined;
  }

  /**
   * @description This is called each time the TrasformTool is transforming the element
   */
  onTransformed() {
    this.recalculateWGS84Coords();
    this.createGeometry();
  }

  /**
   * @description Add a point to the line. isWGS84 declare if it is in WGS84 coordinates or in local space
   * @param {THREE.Vector3} point
   * @param {boolean} isWGS84
   */
  addPoint(point: THREE.Vector3, isWGS84: boolean) {
    // If we manage to add the same point twice (like a double click) ignore it
    if (this.points.length && this.points[this.points.length - 1].point.equals(point)) {
      return;
    }

    this.insertPointAt(point, this.points.length, isWGS84);
  }

  /**
   * Inserts a new point to the points collection at the specified index.
   * @param point
   * @param index
   * @param isWGS84
   */
  insertPointAt(point: THREE.Vector3, index: number, isWGS84 = false) {
    // If it is WGS84 coordinates, we need to trasform it to local space
    if (isWGS84) {
      const tempHeight = this.availableMAS() ? this.calculateLocalHeight(point.z)! : point.z;
      let tempPoint = CoordinateManager.getInstance().convertToProject(point.x, point.y, 'wgs84');
      tempPoint = CoordinateManager.getInstance().projectToLocal(tempPoint.x, tempPoint.y);

      point.x = tempPoint.x;
      point.y = tempPoint.y;
      point.z = tempHeight;
    }

    const worldPoints = this.getWorldPoints();

    worldPoints.splice(index, 0, point);
    const midPoint = mathHelpers.calcMidPoint(worldPoints);

    this.points = worldPoints.map((woldPoint, i) => ({
      id: i < index ? this.points[i].id : i === index ? v4() : this.points[i - 1].id,
      point: new THREE.Vector3(
        woldPoint.x - midPoint.x,
        woldPoint.y - midPoint.y,
        woldPoint.z - midPoint.z,
      ),
    }));

    this.position.set(midPoint.x, midPoint.y, midPoint.z);

    this.createGeometry();

    // Calculate the wgs84 coordinate
    const wgs84Point = this.calculateWGS84Coords(new THREE.Vector2(point.x, point.y))!;
    // If no MAS is available, keep the original value.
    const pointMAS = this.availableMAS() ? this.calculateMAS(point.z) : point.z;
    this.wgs84Coords.splice(index, 0, new THREE.Vector3(wgs84Point.x, wgs84Point.y, pointMAS));
  }

  /**
   * @param index
   * @description Remove a point based on index
   */
  removePoint(index: number) {
    if (index < 0 || index >= this.points.length) {
      return;
    }
    if (index > -1) {
      this.points.splice(index, 1);
    }

    this.createGeometry();
    this.recalculateWGS84Coords();
  }

  /**
   * Gets a value indicating whether the point at the specified index is an end point.
   * @param index
   */
  isEndPoint(index: number) {
    return index === 0 || index === this.points.length - 1;
  }

  /**
   * Gets the index of the opposite end point, given the index of the other end point.
   * If specified point is not an end point, an error is thrown.
   * @param {number} endPointIndex
   */
  resolveOppositeEndPointIndex(endPointIndex: number) {
    if (this.isEndPoint(endPointIndex) === false) {
      throw new Error(`Specified index ${endPointIndex} is not an end point.`);
    }

    return endPointIndex === 0 ? this.points.length - 1 : 0;
  }

  /**
   * Removes point beeing snapped from points list, if any.
   */
  removeSnapPoint() {
    if (this.isSnapped()) {
      this.points.splice(this.snapping.index, 1);
      this.unsnap();
    }
  }

  /**
   * Gets a value indicating whether the geometry is currently snapped.
   */
  isSnapped() {
    return this.snapping.snapped;
  }

  /**
   * Unsnaps area back to line.
   */
  unsnap() {
    this.snapping.snapped = false;
    this.snapping.index = -1;
  }

  /**
   * Returns a value indicating whether the point at specified index is snapped to
   * the other end point. Snapping is done when geometry is a line with enough
   * points, and the distance between endpoints is max 10 px.
   * @param index
   * @param clientX
   * @param clientY
   * @param camera
   */
  snap(index: number, clientX: number, clientY: number, camera: SkyviewCamera) {
    if (this.isArea || this.points.length < 4 || this.isEndPoint(index) === false) {
      this.unsnap();
      return false;
    }

    // Get screen distance between end points.
    const oppositeEndPointIndex = this.resolveOppositeEndPointIndex(index);
    const distance = this.screenDistanceToPoint(oppositeEndPointIndex, clientX, clientY, camera);

    // Snap end points?
    if (distance <= 10) {
      this.snapping.snapped = true;
      this.snapping.index = index;

      // Make end point at snapping index identical to other end point.
      // Now we have two identical endpoints, and a ghost sphere at the same point.
      // However, the ghost sphere is not rendered having these points snapped to each other.
      // (If saving having the geometry snapped, the snapped end point is removed)
      this.points[this.snapping.index] = {
        id: v4(),
        point: this.points[oppositeEndPointIndex].point.clone(),
      };

      this.createGeometry();
      return true;
    }

    this.unsnap();
    return false;
  }

  createGeometry() {
    this.clearGeometry();

    // Lines
    this.lines = [];
    const addLine = (p1: THREE.Vector3, p2: THREE.Vector3) => {
      const newLine = createLine(p1, p2, this.materials.line);
      newLine.renderOrder = lineRenderOrder;
      this.lines.push(newLine);
      if (this.renderLines) {
        this.add(newLine);
      }
    };

    for (let i = 0; i < this.points.length - 1; i++) {
      addLine(this.points[i].point, this.points[i + 1].point);
    }

    if (this.isArea || this.isSnapped()) {
      addLine(this.points[this.points.length - 1].point, this.points[0].point);
      this.areaMesh = this.createArea();
      this.add(this.areaMesh);
    }

    this.createSpheres();
    this.calculateDist();

    this.onMetaDataUpdated?.();

    if (this.labelsGroup) {
      heightLabelUtils.resetLabelGroup(this);
      this.refreshLabelsGroup();
    }
  }

  createSpheres() {
    // Editable spheres
    this.selectionSpheres = this.points.map((point) => new Sphere(point.point));

    // Create one ghost sphere in the middle of each line.
    for (const line of this.lines) {
      const x0 = line.geometry.attributes.position.getX(0);
      const y0 = line.geometry.attributes.position.getY(0);
      const z0 = line.geometry.attributes.position.getZ(0);

      const x1 = line.geometry.attributes.position.getX(1);
      const y1 = line.geometry.attributes.position.getY(1);
      const z1 = line.geometry.attributes.position.getZ(1);

      const p1 = new Vector3(x0, y0, z0);
      const p2 = new Vector3(x1, y1, z1);
      const midPoint = mathHelpers.calcMidPoint([p1, p2]);
      this.selectionSpheres.push(new Sphere(midPoint, SphereSprite.DotGray));
    }

    this.showSpheres();
  }

  showSpheres() {
    if (this.isSelected() && this.editable && this.isSnapped() === false) {
      for (const sphere of this.selectionSpheres) {
        this.add(sphere);
        sphere.onAnimate();
      }
      this.selectionSpheres.forEach((sphere) => this.add(sphere));
    }
  }

  hideSpheres(dispose = false) {
    this.selectionSpheres.forEach((sphere) => {
      this.remove(sphere);
      if (dispose) {
        sphere.dispose();
      }
    });
  }

  clearGeometry() {
    this.hideSpheres(true);

    this.lines.forEach((line) => {
      this.remove(line);
      line.geometry.dispose();
    });

    if (this.areaMesh) {
      this.remove(this.areaMesh);
      this.areaMesh.geometry.dispose();
    }

    this.areaMesh = undefined;
    this.area = undefined;
    this.planeArea = undefined;
  }

  getVertices(points: THREE.Vector3[]): number[] {
    return points.reduce((prev: number[], cur: THREE.Vector3) => {
      prev.push(cur.x);
      prev.push(cur.y);
      prev.push(cur.z);
      return prev;
    }, []);
  }

  getTriangles(vertexArray: number[]): number[] {
    return earcut(vertexArray, undefined, 3);
  }

  createArea(offset?: Vector3) {
    const points = isDefined(offset)
      ? this.points.map((x) => ({
          id: x.id,
          point: x.point.clone().add(offset),
        }))
      : this.points;
    const vtxArr = this.getVertices(points.map((x) => x.point));
    const triangles = this.getTriangles(vtxArr);

    const tmpGeom = new THREE.BufferGeometry();
    tmpGeom.setAttribute('position', new THREE.Float32BufferAttribute(vtxArr, 3));
    tmpGeom.setIndex(triangles);

    const area = new THREE.Mesh(tmpGeom, this.materials.area);
    area.renderOrder = areaRenderOrder;

    // Calculate this.area and this.planeArea here as well :)
    this.area = mathHelpers.calculateArea(vtxArr, triangles, false);
    this.planeArea = mathHelpers.calculateArea(vtxArr, triangles, true);

    return area;
  }

  setArea(state: boolean) {
    this.isArea = state;

    // Area set using snapping?
    this.removeSnapPoint();

    this.area = undefined;
    this.planeArea = undefined;

    this.createGeometry();
  }

  onSelect() {
    super.onSelect();

    this.materials.line.color = new THREE.Color(selectionColor);

    if (this.isArea && this.areaMesh) {
      (this.areaMesh.material as THREE.MeshBasicMaterial).color = new THREE.Color(selectionColor);
    }

    this.showSpheres();
  }

  onAnimate(camera: SkyviewCamera) {
    this.selectionSpheres.forEach((sphere) => {
      sphere.onAnimate();
    });
  }

  onDeselect() {
    super.onDeselect();

    this.materials.line.color = new THREE.Color(lineColor);

    if (this.isArea && this.areaMesh) {
      (this.areaMesh.material as THREE.MeshBasicMaterial).color = new THREE.Color(areaColor);
    }
    this.hideSpheres();
  }

  calculateDist() {
    const projectecPoints = this.points.map(
      (point) => new THREE.Vector2(point.point.x, point.point.y),
    );
    this.distance = mathHelpers.calcPointDistances(
      this.points.map((x) => x.point),
      this.isArea,
    );
    this.planeDistance = mathHelpers.calcPointDistances(projectecPoints, this.isArea);

    this.slope =
      this.points.length > 1
        ? vectorToSlopePercent(this.points.at(-1)!.point.clone().sub(this.points.at(-2)!.point))
        : 0;
  }

  /**
   * @description Calculate the geograhpic coords for the points of the element
   */
  recalculateWGS84Coords() {
    const worldPoints = this.getWorldPoints();
    this.wgs84Coords = worldPoints.map((point, idx) => {
      const wgs84Coord = this.calculateWGS84Coords(new THREE.Vector2(point.x, point.y))!;
      const pointMAS = this.availableMAS() ? this.calculateMAS(point.z) : this.wgs84Coords[idx].z;
      return new THREE.Vector3(wgs84Coord.x, wgs84Coord.y, pointMAS);
    });
  }

  mouseOver(idx: number) {
    // reset last selectionsphere
    if (this.highlighted > -1) {
      this.selectionSpheres[this.highlighted].onNotHover();
      this.highlighted = -1;
      publish(SubscriptionTopic.AssetHighlightedIndexChanged);
    }

    if (idx !== -1) {
      this.highlighted = idx;
      this.selectionSpheres[idx].onHover();
      publish(SubscriptionTopic.AssetHighlightedIndexChanged);
    }
  }

  resetEditing(idx: number) {
    this.unsnap();

    if (!this.oldPickedPosition) {
      return;
    }

    this.recalculate(idx, this.oldPickedPosition);
    this.oldPickedPosition = undefined;
  }

  recalculate(idx: number, newPoint: THREE.Vector3) {
    if (!newPoint || idx >= this.points.length) {
      return;
    }

    this.points[idx].point.x = newPoint.x - this.position.x;
    this.points[idx].point.y = newPoint.y - this.position.y;
    this.points[idx].point.z = newPoint.z - this.position.z;

    this.recalculateWGS84Coords();
    this.createGeometry();
  }

  pick(x: number, y: number, camera: SkyviewCamera) {
    // Convert NDC mouse position to screen-pixel coords using the same approach
    // as volume-boundary-polygon's findClosestVertexPoint and pick.ts pixel-sort.
    const ray = camera.getRay(x, y);
    const mouseScreenPos = camera.getScreenPosition(ray.ray.origin.clone().add(ray.ray.direction));

    const PICK_THRESHOLD_SQ = 144; // 12 px radius
    let closestIndex = -1;
    let closestDistSq = PICK_THRESHOLD_SQ;

    for (let i = 0; i < this.selectionSpheres.length; i++) {
      const sphere = this.selectionSpheres[i];
      if (sphere.parent !== this) {
        continue;
      }
      const worldPos = sphere.getWorldPosition(new THREE.Vector3());
      const screenPos = camera.getScreenPosition(worldPos);
      const distSq = screenPos.distanceToSquared(mouseScreenPos);
      if (distSq < closestDistSq) {
        closestDistSq = distSq;
        closestIndex = i;
        this.oldPickedPosition = new THREE.Vector3(
          sphere.position.x + this.position.x,
          sphere.position.y + this.position.y,
          sphere.position.z + this.position.z,
        );
      }
    }

    return closestIndex;
  }

  /**
   * Takes an index pointing to a ghost sphere and converts it into an
   * editable sphere positioned at the specified point.
   *
   * If ghost sphere is converted, the geometry is recreated (new ghost
   * spheres will be rendered) and index of the inserted sphere is returned.
   *
   * Otherwise, specified sphere index is returned.
   * @param sphereIndex
   * @param renderAtPoint
   */
  convertGhostSphere(sphereIndex: number, renderAtPoint?: THREE.Vector3) {
    const sphere = this.selectionSpheres[sphereIndex];
    if (sphere.isGhost === false) {
      return sphereIndex;
    }

    // Calculate index in this.points where to insert the new point;
    //
    // Example: area of 4 points (this.points), this.selectionSpheres =
    //  Regular: [0, 1, 2, 3,
    //  Ghosts:   4, 5, 6, 7]
    //
    // Clicking on ghost 5 (between point 1 and 2) a new point should
    // be inserted at index 2 in this.points: 5-(4-1) = 2.
    const insertIndex = sphereIndex - (this.points.length - 1);

    // Insert new point and recreate geometry.
    this.insertPointAt(renderAtPoint ?? sphere.getWorldPosition(new Vector3()), insertIndex, false);

    // Return index of point inserted.
    return insertIndex;
  }

  /**
   * @param idx
   */
  getPointWorldPosition(idx: number) {
    const worldPoints = this.getWorldPoints();
    if (idx === -1 || worldPoints.length < 0) {
      return;
    }
    return worldPoints[idx];
  }

  /**
   * Converts position of point specified by the given index to a 2D screen point.
   * @param pointIndex
   * @param camera
   */
  getPointScreenPosition(pointIndex: number, camera: SkyviewCamera) {
    const worldPoint = this.getPointWorldPosition(pointIndex)!;
    const screenPoint = camera.getScreenPosition(worldPoint);
    return screenPoint;
  }

  /**
   * Calculates 2D screen distance between a point on screen (x,y)
   * and the geometry point given by the specified index.
   * @param {number} pointIndex Index of point
   * @param {number} screenX Screen X position
   * @param {number} screenY Screen Y position
   * @param {Camera} camera Camera instance
   */
  screenDistanceToPoint(
    pointIndex: number,
    screenX: number,
    screenY: number,
    camera: SkyviewCamera,
  ) {
    // Two screen points
    const sp1 = this.getPointScreenPosition(pointIndex, camera);
    const sp2 = new THREE.Vector2(screenX, screenY);

    return sp1.distanceTo(sp2);
  }

  /**
   * @description Returns the points in local space
   * @returns {Array<THREE.Vector3>}
   */
  getLocalPoints() {
    return this.points;
  }

  /**
   * @description Returns the points in world space
   * @returns {Array<THREE.Vector3>}
   */
  getWorldPoints() {
    return this.points.map((point) => {
      return new THREE.Vector3(
        point.point.x + this.position.x,
        point.point.y + this.position.y,
        point.point.z + this.position.z,
      );
    });
  }

  /**
   * @description Calculates the lines position based on wgs84 coordinates
   */
  calculateLocalPosition(): Point2d | undefined {
    // Reset all of the positions and points
    this.points = [];
    this.position.set(0, 0, 0);

    // Make a temp copy of the wgs84 coords
    const tmpWgs84Coords = this.wgs84Coords.map((coords) => coords);
    this.wgs84Coords = [];

    // Calculate the new position
    tmpWgs84Coords.map((point, idx) => {
      this.addPoint(new THREE.Vector3(point.x, point.y, point.z), true);
    });

    return;
  }

  setDistance(dist: number) {
    this.distance = dist;
  }

  getDistance() {
    return this.distance;
  }

  getSlope() {
    return this.slope;
  }

  getPlaneDistance() {
    return this.planeDistance;
  }

  getArea() {
    return this.area;
  }

  getPlaneArea() {
    return this.planeArea;
  }

  async onChange() {
    // If geometry is snapped (from line to area) make it an area.
    // This will also remove the point beeing snapped.
    // Important to remove this point before super.onChange() that
    // persists the geometry to database.
    const isSnapped = this.isSnapped();
    if (isSnapped) {
      this.setArea(true);
    }

    await super.onChange();

    // After save, update entire asset list to make asset render in areas
    // section instead of lines.
    if (isSnapped) {
      AssetManager.instance.publishAssetListChanged();
    }
  }

  refreshLabelsGroup() {
    if (!this.labelsGroup) {
      return;
    }

    if (this.labelsSettings?.showHeightLabels) {
      for (const point of this.getLocalPoints()) {
        heightLabelUtils.createLabel(
          this.labelsGroup,
          point.point,
          this.labelsSettings.color,
          this.labelsSettings.fontSize,
          this.labelsSettings.sizeAttenuation ?? true,
          this.labelsSettings.decimalPlaces,
        );
      }
    }

    if (this.labelsSettings?.showLengthLabels) {
      const localPoints = this.getLocalPoints();
      const shouldConnectLastAndFirstPoint = this.isArea;
      for (const [a, b] of consequetivePairIterator(localPoints, shouldConnectLastAndFirstPoint)) {
        heightLabelUtils.createLengthLabel(
          this.labelsGroup,
          a.point,
          b.point,
          this.labelsSettings.color,
          this.labelsSettings.fontSize,
          this.labelsSettings.sizeAttenuation ?? true,
          this.labelsSettings.decimalPlaces,
        );
      }
    }
  }

  extractMeshInformation(mesh?: Mesh): { vertices: number[]; indices: number[] } {
    const vertices = this.getVertices(this.getWorldPoints());
    const indices = this.getTriangles(vertices);

    return {
      vertices,
      indices,
    };
  }

  setOpacity(opacity: number) {
    if (this.areaMesh?.material) {
      const materials: Material[] = Array.isArray(this.areaMesh.material)
        ? this.areaMesh.material
        : [this.areaMesh.material];
      for (const material of materials) {
        material.opacity = opacity;
        material.transparent = true;
      }
      this.materials.line.transparent = opacity < 1;
    } else {
      this.materials.line.opacity = opacity;
    }
  }

  equals(geometry: Geometry) {
    const sameId = geometry.assetId === this.assetId;
    const sameVolumeSourceIds = _.isEqual(
      this.skyMapVolumeSourceIds.sort(),
      geometry.skyMapVolumeSourceIds.sort(),
    );

    return sameId && sameVolumeSourceIds;
  }
}
